import { unreachable } from "@mdotm/utils/runtime";
import type { ComparisonOperatorDTOKeywordEnum } from "@eu.mdotm.api/smart-workspaces";
import { Controller } from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, renderNodeOrFn, useDeepEqualEffect } from "@mdotm/shadcn/legacy/react-extensions";
import { useState, useEffect } from "react";

export type OnChangeComparatorParams<T> =
	| {
			data: [T | null, T | null];
			comparator: Extract<ComparisonOperatorDTOKeywordEnum, "btw">;
	  }
	| {
			data: [T | null];
			comparator: Exclude<ComparisonOperatorDTOKeywordEnum, "btw">;
	  }
	| {
			data: [T[]];
			comparator: Extract<ComparisonOperatorDTOKeywordEnum, "in">;
	  };

type ComparatorProps<T> = {
	component: NodeOrFn<{ onChange(data: T[] | T | null): void; value: T[] | T | null }>;
	onChange(params: OnChangeComparatorParams<T>): void;
	comparator: ComparisonOperatorDTOKeywordEnum;
	initialValue?: [T | null] | [T | null, T | null] | [T[]];
};

export function Comparator<T>(props: ComparatorProps<T>) {
	const [state, setState] = useState(props.initialValue);

	useDeepEqualEffect(() => {
		props.onChange({ data: state as any, comparator: props.comparator });
	}, [state]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <reset state when comparator change>
	useEffect(() => {
		setState(() => props.initialValue);
		return () => {
			setState(() => props.initialValue);
		};
	}, [props.comparator]);

	switch (props.comparator) {
		case "btw":
			return (
				<>
					<Controller
						value={(state?.[0] ?? null) as T | null}
						onChange={(value) =>
							setState((latest) => {
								return [value as T, latest?.[1] ?? null];
							})
						}
					>
						{({ value, onCommit: onChange }) => renderNodeOrFn(props.component, { value, onChange: onChange as any })}
					</Controller>
					<Controller
						value={(state?.[1] ?? null) as T | null}
						onChange={(value) =>
							setState((latest) => {
								return [(latest?.[0] ?? null) as T | null, value as T];
							})
						}
					>
						{({ value, onCommit: onChange }) => renderNodeOrFn(props.component, { value, onChange: onChange as any })}
					</Controller>
				</>
			);
		case "eq":
		case "exist":
		case "lt":
		case "lte":
		case "gt":
		case "gte":
		case "contains":
		case "in":
		case "ne":
			return (
				<Controller
					value={(state?.[0] ?? (props.comparator === "in" ? [] : null)) as T[] | T | null}
					onChange={(newValue) => setState([newValue as any])}
				>
					{({ value, onCommit: onChange }) => renderNodeOrFn(props.component, { value, onChange })}
				</Controller>
			);
		default:
			unreachable(props.comparator);
	}
}
